import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError, tap, finalize, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';

// ─── GraphQL Operations ───────────────────────────────────────────────────────

const LOGIN_QUERY = gql`
  query Login($email: String!, $password: String!) {
    login2(userName: $email, password: $password) {
      accessToken
      refreshToken
    }
  }
`;

const REFRESH_TOKEN_QUERY = gql`
  query RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      accessToken
      refreshToken
    }
  }
`;

const ME_QUERY = gql`
  query Me {
    me {
      id
      firstName
      lastName
      userName
      email
      mobileNumber
      role {
        id
        title
        permissions
        allowedApps
      }
    }
  }
`;

// ─── Models ───────────────────────────────────────────────────────────────────

export interface OperatorRole {
  id: string;
  title: string;
  permissions: string[];
  allowedApps: string[];
}

export interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  mobileNumber?: string;
  role?: OperatorRole;
  accessToken: string;
}

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AdminAuthService {
  private currentUserSubject = new BehaviorSubject<AdminUser | null>(
    this._loadStoredUser()
  );
  public currentUser$ = this.currentUserSubject.asObservable();
  private refreshRequest$: Observable<string> | null = null;

  constructor(private apollo: Apollo, private router: Router) {}

  get currentUserValue(): AdminUser | null {
    return this.currentUserSubject.value;
  }

  get isLoggedIn(): boolean {
    return !!this.currentUserValue && !!localStorage.getItem('access_token');
  }

  /** Authenticate with the admin-api GraphQL endpoint */
  login(email: string, password: string): Observable<AdminUser> {
    return this.apollo
      .query<{ login2: { accessToken: string; refreshToken: string } }>({
        query: LOGIN_QUERY,
        variables: { email, password },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((result) => {
          if (result.error) {
            throw result.error;
          }
          const tokens = result.data?.['login2'];
          if (!tokens) {
            throw new Error('Login failed: no token returned');
          }
          return tokens;
        }),
        tap(({ accessToken, refreshToken }) => {
          localStorage.setItem('access_token', accessToken);
          localStorage.setItem('refresh_token', refreshToken);
        }),
        map(({ accessToken }) => {
          const user: AdminUser = {
            id: '',
            firstName: '',
            lastName: '',
            userName: email,
            email,
            accessToken,
          };
          this._storeUser(user);
          return user;
        }),
        catchError((err) => {
          console.error('[AdminAuthService] login error', err);
          return throwError(() => err);
        })
      );
  }

  /** Fetch the operator profile and enrich the stored user */
  fetchProfile(): Observable<AdminUser> {
    return this.apollo
      .query<{ me: any }>({
        query: ME_QUERY,
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((result) => {
          const me = result.data?.['me'];
          if (!me) throw new Error('Profile not found');
          const token = localStorage.getItem('access_token') ?? '';
          const user: AdminUser = { ...me, accessToken: token };
          this._storeUser(user);
          return user;
        }),
        catchError((err) => {
          console.error('[AdminAuthService] fetchProfile error', err);
          return throwError(() => err);
        })
      );
  }

  /** Refresh access token using stored refresh token */
  refreshAccessToken(): Observable<string> {
    if (this.refreshRequest$) return this.refreshRequest$;

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token stored'));
    }

    this.refreshRequest$ = this.apollo
      .query<{ refreshToken: { accessToken: string; refreshToken: string } }>({
        query: REFRESH_TOKEN_QUERY,
        variables: { refreshToken },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((result) => {
          const tokens = result.data?.['refreshToken'];
          if (!tokens) throw new Error('Token refresh failed');
          localStorage.setItem('access_token', tokens.accessToken);
          localStorage.setItem('refresh_token', tokens.refreshToken);
          const current = this.currentUserValue;
          if (current) {
            this._storeUser({ ...current, accessToken: tokens.accessToken });
          }
          return tokens.accessToken;
        }),
        catchError((err) => {
          this.logout();
          return throwError(() => err);
        }),
        finalize(() => { this.refreshRequest$ = null; }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this.refreshRequest$;
  }

  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
    this.apollo.client.clearStore();
    this.router.navigate(['/auth/login']);
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  private _storeUser(user: AdminUser): void {
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private _loadStoredUser(): AdminUser | null {
    try {
      const raw = localStorage.getItem('currentUser');
      return raw ? (JSON.parse(raw) as AdminUser) : null;
    } catch {
      return null;
    }
  }
}
